import {
  Activity,
  ArrowDown,
  ArrowUp,
  Flame,
  MessageCircle,
  MoveDiagonal,
  RotateCcw,
  Search,
  Shrink,
  Sparkles,
  TrendingUp,
  Trophy,
  UserPlus,
  WandSparkles,
  Zap,
} from "lucide-react"
import React from "react"
import type { BrackyFeature, Scenario } from "../data/bracky-features-data"
import { WHATS_BRACKY_FAQ_ID } from "../LandingSectionFAQ"

const iconMap: Record<string, React.ComponentType<{ className?: string }>> = {
  Bet: ArrowUp,
  Sell: ArrowDown,
  Bundle: Shrink,
  Adjust: MoveDiagonal,
  Analyze: Sparkles,
  Update: Zap,
  Edge: Search,
  Predict: TrendingUp,
  Follow: Activity,
  Invite: UserPlus,
  "Copy Position": RotateCcw,
  Challenge: Trophy,
  "Creator Brackets": WandSparkles,
  "Pop Ups": Zap,
  "Live Events": Flame,
}

export function getScenarioIcon(label: string) {
  return iconMap[label] || MessageCircle
}

export function renderScenarioDescription(
  scenario: Scenario | null,
  feature: BrackyFeature,
  isMobile = false,
): React.ReactNode {
  const desktopDescription = scenario?.description ?? ""

  interface WithMobileDesc {
    descriptionMobile?: string | React.ReactNode
  }
  const mobileDescriptionExplicit =
    (scenario as WithMobileDesc | null)?.descriptionMobile ?? (feature as unknown as WithMobileDesc).descriptionMobile

  let mobileDescription: string | React.ReactNode | undefined = mobileDescriptionExplicit

  if (isMobile && !mobileDescription) {
    const getShortText = (text: string) => {
      const firstSentence = text.split(/\.\s/)[0]
      if (!firstSentence) return "…"
      if (firstSentence.length <= 90) return `${firstSentence}${firstSentence.endsWith(".") ? "" : "…"}`
      return `${text.slice(0, 90).trimEnd()}…`
    }
    mobileDescription = getShortText(desktopDescription)
  }

  const description = isMobile ? (mobileDescription ?? desktopDescription) : desktopDescription

  const highlightBracky = (text: string): React.ReactNode[] =>
    text.split(/(@bracky)/gi).map((part, idx) => {
      if (part.toLowerCase() === "@bracky") {
        return (
          <span
            // biome-ignore lint/suspicious/noArrayIndexKey: Parts alternate between text/@bracky - index needed for position
            key={`bracky-${idx}`}
            className="font-semibold text-[#0074FF]"
          >
            {part}
          </span>
        )
      }
      return part
    })

  if ((scenario?.label === "Bet" || scenario?.label === "Sell") && String(description).includes("$BRACKY")) {
    const highlightTarget = "$BRACKY"

    const processedDescription =
      scenario?.label === "Bet" && typeof description === "string" && description.includes(highlightTarget)
        ? description.split(highlightTarget).map((part, idx, arr) => (
            <span
              // biome-ignore lint/suspicious/noArrayIndexKey: Text parts split by $BRACKY - index needed for position
              key={`part-${idx}`}
            >
              {part}
              {idx < arr.length - 1 && (
                <span className="font-semibold bg-linear-to-r from-[#0074FF] to-[#30DDFF] bg-clip-text text-transparent">
                  {highlightTarget}
                </span>
              )}
            </span>
          ))
        : description

    const finalDescription = Array.isArray(processedDescription)
      ? processedDescription.map((node, idx) =>
          typeof node === "string"
            ? highlightBracky(node).map((n, i) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: Nested highlighting - index needed for unique keys
                <React.Fragment key={`frag-${idx}-${i}`}>{n}</React.Fragment>
              ))
            : node,
        )
      : typeof processedDescription === "string"
        ? highlightBracky(processedDescription)
        : processedDescription

    return (
      <>
        {finalDescription}{" "}
        <button
          type="button"
          onClick={() => {
            const faqId = WHATS_BRACKY_FAQ_ID
            const el = document.getElementById(`faq-${faqId}`)
            if (el) {
              el.scrollIntoView({ behavior: "smooth", block: "center" })
            } else {
              document.getElementById("faqs")?.scrollIntoView({ behavior: "smooth" })
            }
            window.dispatchEvent(new CustomEvent("openFAQ", { detail: { faqId } }))
          }}
          className="text-[#22FF65] underline decoration-[#22FF65] font-medium text-[0.9375rem] sm:text-[1.0625rem] lg:text-[1.0625rem] inline-flex items-center gap-[0.09rem]"
        >
          What's $BRACKY? <ArrowDown className="w-4 h-4 translate-y-[0.025rem]" />
        </button>
      </>
    )
  }

  if (
    isMobile &&
    typeof description === "string" &&
    description.includes("Ask @bracky about friends' positions and get a full breakdown")
  ) {
    return (
      <>
        Ask @bracky about friends' positions
        <br />
        and get a full breakdown on their betting game.
      </>
    )
  }

  return description
}
